import { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getUserDetailsCache, isLoggedIn } from "../../../utils/authHelpers";
import { PDFDownloadLink } from "@react-pdf/renderer";
import UserReportPDFTemplate from "../../../utils/UserReportPDFTemplate";
import useUserReportDetails from "../../hooks/UseUserReportDetails";
import UserReportViewCard from "./UserReportViewCard";
import Header from "../../common/Header";
import Footer from "../../common/Footer";
import UserReportViewSkeleton from "../../Skeletons/UserReportViewSkeleton";
import {
  FaArrowLeft,
  FaFilePdf,
  FaExclamationTriangle,
  FaSearch,
  FaHome,
} from "react-icons/fa";

const UserReportView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { reportDetails, loading, error } = useUserReportDetails(id);
  const userDetails = getUserDetailsCache();

  // Authentication and authorization checks
  useEffect(() => {
    // Redirect to public tracking page if not authenticated
    if (!isLoggedIn() && id) {
      navigate(`/report/track/${id}`, { replace: true });
      return;
    }

    // Check if user has permission to view this report
    if (
      reportDetails &&
      reportDetails.reporterDetails.email !== userDetails?.email
    ) {
      navigate("/unauthorized");
    }
  }, [id, navigate, reportDetails, userDetails]);

  // If not authenticated, don't render anything (redirect handled by useEffect)
  if (!isLoggedIn()) {
    return null;
  }

  const renderContent = () => {
    // Case 1: Error loading report
    if (!loading && error) {
      return renderErrorState();
    }

    // Case 2: Report not found
    if (!loading && !reportDetails) {
      return renderNotFoundState();
    }

    // Case 3: Loading or success state
    return (
      <div className="relative">
        <Header mode="light" className="border-b border-gray-3" />
        <div className="w-full max-w-5xl mx-auto px-4 py-6">
          {renderNavigation()}
          <div className="bg-gray-50 rounded-lg shadow-sm p-4 md:p-6">
            {loading ? (
              <UserReportViewSkeleton />
            ) : (
              <UserReportViewCard report={reportDetails} />
            )}
          </div>
        </div>
        <Footer mode="dark" type="short" />
      </div>
    );
  };

  const renderNavigation = () => (
    <div className="flex justify-between items-center mb-6">
      <button
        className="flex items-center gap-2 py-2 px-4 text-gray-7 hover:bg-gray-100 rounded-lg transition-colors"
        onClick={() => navigate("/dashboard")}
      >
        <FaArrowLeft />
        <span className="font-medium">Back to Dashboard</span>
      </button>

      {renderPdfButton()}
    </div>
  );

  const renderPdfButton = () => {
    if (loading || !reportDetails) {
      return (
        <button className="flex items-center gap-2 bg-gray-4 text-white py-2 px-4 rounded-lg font-semibold cursor-not-allowed">
          <FaFilePdf />
          Export PDF
        </button>
      );
    }

    return (
      <PDFDownloadLink
        document={<UserReportPDFTemplate report={reportDetails} />}
        fileName={`Report-${
          reportDetails.reportId
        }_${new Date().toISOString()}.pdf`}
        className="flex items-center gap-2 bg-danger text-white py-2 px-4 rounded-lg font-semibold hover:bg-red-700 transition-colors"
      >
        <FaFilePdf />
        Export PDF
      </PDFDownloadLink>
    );
  };

  const renderErrorState = () => (
    <ErrorLayout
      icon={<FaExclamationTriangle className="text-4xl text-danger" />}
      title="Error Loading Report"
      message="We're having trouble retrieving the report information. This could be due to network issues or the report may no longer be available."
      actions={[
        {
          label: "Try Again",
          icon: <FaArrowLeft className="text-sm" />,
          onClick: () => window.location.reload(),
          primary: false,
        },
        {
          label: "Dashboard",
          icon: <FaHome className="text-sm" />,
          onClick: () => navigate("/dashboard"),
          primary: true,
        },
      ]}
    />
  );

  const renderNotFoundState = () => (
    <ErrorLayout
      icon={<FaSearch className="text-4xl text-gray-7" />}
      title="Report Not Found"
      message="The report you're looking for doesn't exist or you may not have permission to view it. Please check the report ID and try again."
      actions={[
        {
          label: "Go Back",
          icon: <FaArrowLeft className="text-sm" />,
          onClick: () => navigate(-1),
          primary: false,
        },
        {
          label: "Dashboard",
          icon: <FaHome className="text-sm" />,
          onClick: () => navigate("/dashboard"),
          primary: true,
        },
      ]}
    />
  );
  type ActionProps = {
    label: string;
    icon: React.ReactNode;
    onClick: () => void;
    primary: boolean;
  };

  type ErrorLayoutProps = {
    icon: React.ReactNode;
    title: string;
    message: string;
    actions: ActionProps[];
  };

  // Generic error layout component for different error states
  const ErrorLayout = ({ icon, title, message, actions }: ErrorLayoutProps) => (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header mode="light" className="border-b border-gray-3" />
      <div className="flex-grow flex justify-center items-center p-4">
        <div className="bg-white rounded-xl shadow-md p-8 max-w-md w-full">
          <div className="flex flex-col items-center text-center">
            <div className="bg-gray-100 p-4 rounded-full mb-6">{icon}</div>
            <h2 className="text-2xl font-bold text-gray-7 mb-3">{title}</h2>
            <p className="text-gray-5 mb-6">{message}</p>
            <div className="flex flex-col sm:flex-row gap-3 w-full">
              {actions.map((action, index) => (
                <button
                  key={index}
                  onClick={action.onClick}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 ${
                    action.primary
                      ? "bg-gray-7 text-white hover:bg-gray-6"
                      : "bg-white border-2 border-gray-7 text-gray-7 hover:bg-gray-100"
                  } rounded-lg font-medium transition-colors`}
                >
                  {action.icon}
                  {action.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
      <Footer mode="dark" type="short" />
    </div>
  );

  return renderContent();
};

export default UserReportView;
